import {
  nodeLabel,
  truncateLabel,
  type MindmapNode,
} from '@/lib/mindmap-markdown';

/**
 * The building blocks of the mindmap layout: an outline turned into a sized
 * work tree, and a "side tree" - a set of sibling subtrees laid out as a
 * horizontal tidy tree fanning out from one node, to its left or its right.
 * `roadmapLayout.ts` hangs one of these off each side of every concept.
 *
 * Every label sits on its own row in its own column, so two boxes can only
 * meet if the arithmetic here is wrong, which the unit tests check directly.
 * Pure: label sizes come from the injected `measure`.
 */

export type MindmapSide = 'root' | 'left' | 'right';

/** Type aliases, not interfaces: React Flow's `data` must be a plain record. */
export type LaidOutNode = {
  id: string;
  label: string;
  /** Uncapped text, for the accessible name and the tooltip. */
  fullLabel: string;
  depth: number;
  /**
   * Index of the concept (top-level heading) this node belongs to. It picks
   * the colour and is the concept's step on the roadmap; -1 for the main node.
   */
  branch: number;
  side: MindmapSide;
  parentId: string | null;
  /** Centre of the node box. */
  x: number;
  y: number;
  w: number;
  h: number;
};

export type LaidOutEdge = {
  id: string;
  source: string;
  target: string;
  branch: number;
  /** Depth of the target node. */
  depth: number;
  side: Exclude<MindmapSide, 'root'>;
  /** `spine`: the roadmap path between steps. `tree`: parent to child. */
  kind: 'tree' | 'spine';
};

export interface MindmapLayout {
  nodes: LaidOutNode[];
  edges: LaidOutEdge[];
}

export type MeasureLabel = (
  label: string,
  depth: number
) => { w: number; h: number };

/**
 * A safety cap, not a design limit: a label wraps onto as many lines as it
 * needs, so real headings are never cut. Only runaway text (a paragraph
 * pasted as a heading) is cut, at a word boundary, past this many characters.
 */
export const MAX_LABEL_CHARS = 200;
/** Horizontal space between a node and the next column. */
export const COLUMN_GAP = 56;
/** Vertical space between sibling subtrees. */
export const ROW_GAP = 10;
/** Extra vertical space between top-level subtrees on one side. */
export const BRANCH_GAP = 18;

export interface WorkNode {
  id: string;
  label: string;
  fullLabel: string;
  depth: number;
  branch: number;
  side: MindmapSide;
  parentId: string | null;
  w: number;
  h: number;
  children: WorkNode[];
  leaves: number;
  /** Height of this node's subtree, including gaps between children. */
  span: number;
  x: number;
  y: number;
}

interface BuildContext {
  id: string;
  depth: number;
  branch: number;
  parentId: string | null;
  /** Safety cap: labels longer than this are cut with an ellipsis. */
  maxChars?: number;
}

/** Outline -> sized work tree. Ids are dotted paths: `0`, `0.2`, `0.2.1`. */
export function buildWorkTree(
  node: MindmapNode,
  measure: MeasureLabel,
  { id, depth, branch, parentId, maxChars = MAX_LABEL_CHARS }: BuildContext = {
    id: '0',
    depth: 0,
    branch: -1,
    parentId: null,
  }
): WorkNode {
  const fullLabel = nodeLabel(node.content) || 'Untitled';
  const label = truncateLabel(fullLabel, maxChars);
  const { w, h } = measure(label, depth);
  const children = (node.children ?? []).map((child, i) =>
    buildWorkTree(child, measure, {
      id: `${id}.${i}`,
      depth: depth + 1,
      branch: depth === 0 ? i : branch,
      parentId: id,
      maxChars,
    })
  );
  const leaves = children.length
    ? children.reduce((sum, c) => sum + c.leaves, 0)
    : 1;
  return {
    id,
    label,
    fullLabel,
    depth,
    branch,
    side: 'root',
    parentId,
    w,
    h,
    children,
    leaves,
    span: Math.max(h, stackedSpan(children, ROW_GAP)),
    x: 0,
    y: 0,
  };
}

function stackedSpan(nodes: WorkNode[], gap: number): number {
  if (!nodes.length) return 0;
  return nodes.reduce((sum, n) => sum + n.span, 0) + gap * (nodes.length - 1);
}

/**
 * Cut sibling subtrees, in authored order, into a right half (first) and a
 * left half, choosing the cut that best balances leaf counts. A tie keeps the
 * earlier cut, so an odd count puts the extra subtree on the left.
 */
export function splitSides<T extends { leaves: number }>(
  branches: T[]
): { right: T[]; left: T[] } {
  if (branches.length <= 1) return { right: branches, left: [] };
  const total = branches.reduce((sum, b) => sum + b.leaves, 0);
  let best = 1;
  let bestDiff = Infinity;
  let acc = 0;
  for (let k = 1; k < branches.length; k++) {
    acc += branches[k - 1].leaves;
    const diff = Math.abs(acc - (total - acc));
    if (diff < bestDiff) {
      best = k;
      bestDiff = diff;
    }
  }
  return { right: branches.slice(0, best), left: branches.slice(best) };
}

export function tagSide(node: WorkNode, side: MindmapSide) {
  node.side = side;
  node.children.forEach(c => tagSide(c, side));
}

/** Height a side tree needs: its subtrees stacked with `BRANCH_GAP`. */
export function sideSpan(branches: WorkNode[]): number {
  return stackedSpan(branches, BRANCH_GAP);
}

function collectColumnWidths(node: WorkNode, widths: number[]) {
  widths[node.depth] = Math.max(widths[node.depth] ?? 0, node.w);
  node.children.forEach(c => collectColumnWidths(c, widths));
}

/** Top-down: each child block is centred on its parent's slot. */
function placeVertically(node: WorkNode, top: number) {
  if (!node.children.length) {
    node.y = top + node.span / 2;
    return;
  }
  let cursor = top + (node.span - stackedSpan(node.children, ROW_GAP)) / 2;
  for (const child of node.children) {
    placeVertically(child, cursor);
    cursor += child.span + ROW_GAP;
  }
  const first = node.children[0];
  const last = node.children[node.children.length - 1];
  node.y = (first.y + last.y) / 2;
}

/** Columns grow outward from the anchor; the inner edge of a column is shared. */
function placeHorizontally(
  node: WorkNode,
  cx: number,
  columnStart: number[],
  direction: 1 | -1
) {
  node.x = cx + direction * (columnStart[node.depth] + node.w / 2);
  node.children.forEach(c => placeHorizontally(c, cx, columnStart, direction));
}

export interface SideAnchor {
  /** Centre of the node the side tree hangs off. */
  cx: number;
  cy: number;
  /** Half the anchor's width: columns start this far out, plus a gap. */
  halfWidth: number;
}

/**
 * Lay `branches` out as one side tree beside `anchor`, vertically centred on
 * it, growing right (`1`) or left (`-1`). Depth decides the column, so all
 * subtrees on one side share column edges and read as aligned rows.
 */
export function layoutSide(
  branches: WorkNode[],
  { cx, cy, halfWidth }: SideAnchor,
  direction: 1 | -1
) {
  if (!branches.length) return;
  const widths: number[] = [];
  branches.forEach(b => collectColumnWidths(b, widths));
  const columnStart: number[] = [];
  let cursor = halfWidth + COLUMN_GAP;
  for (let depth = branches[0].depth; depth < widths.length; depth++) {
    columnStart[depth] = cursor;
    cursor += (widths[depth] ?? 0) + COLUMN_GAP;
  }
  let top = cy - sideSpan(branches) / 2;
  for (const branch of branches) {
    placeVertically(branch, top);
    placeHorizontally(branch, cx, columnStart, direction);
    top += branch.span + BRANCH_GAP;
  }
}

export function toLaidOutNode(node: WorkNode): LaidOutNode {
  return {
    id: node.id,
    label: node.label,
    fullLabel: node.fullLabel,
    depth: node.depth,
    branch: node.branch,
    side: node.side,
    parentId: node.parentId,
    x: node.x,
    y: node.y,
    w: node.w,
    h: node.h,
  };
}
